import { ReactElement, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import CommentsListPane from '@editor/components/CommentsListPane'
import type { Results2ErrorTab } from '@editor/pages/Results2/hooks/useResults2TabErrorStatuses'
import { TextTabs } from '@ui2/navigation'
import { useAppStore } from '@stores'
import { OVERUTILIZED_CHIP_BACKGROUND } from '../../utils'
import Results2AnchorsList from '../Results2AnchorsList'
import Results2AssemblyList from '../Results2AssemblyList'
import Results2ElementList, { Results2ListItem } from '../Results2ElementList'
import Results2ShearAnchorsList from '../Results2ShearAnchorsList'
import Results2StandardRipsList from '../Results2StandardRipsList'
import { PRIMARY_TABS, RESULTS2_TAB_SHORT_LABEL, WALL_SECONDARY_TABS } from './constants'

type Props = {
  items: Results2ListItem[]

  selectedId?: string
  selectedIds?: string[]
  onSelect?: (id: string) => boolean | void
  onSelectionChange?: (ids: string[]) => boolean | void

  primary: string
  onPrimaryChange: (value: string) => void
  secondary: string
  onSecondaryChange: (value: string) => void
  showTabLabels?: boolean
  /** Primary tabs whose header is tinted red because one of their elements is over-utilized. */
  tabErrorStatuses?: Record<Results2ErrorTab, boolean>

  isStiffeningTab: boolean
  isStandardRipsTab: boolean
  /** Decke/Dach showing the slab surfaces instead of their beams (selected from the 3D scene). */
  isAssemblySurfaceTab: boolean
  isAnchorsTab: boolean
  isShearAnchorsTab: boolean
  groupingEnabled: boolean

  isLoading?: boolean
  emptyMessage?: string

  onCalculate?: () => void
  /** Cancels in-flight results loading; surfaced as an "Abbrechen" footer button. */
  onAbort?: () => void
  /** When true, shows the "Abbrechen" button next to "Berechnen". */
  showAbort?: boolean
  onCopyParentCrossSection?: (childId: string, parentId: string) => void
  copyParentCrossSectionVisibleByChildId?: Record<string, boolean>
  copyParentCrossSectionLoadingChildId?: string | null

  /** List footer “Fokus” switch — drives camera framing from Results2. */
  fokusCamera: boolean
  onFokusCameraChange: (next: boolean) => void
  /** List footer “Transparenz” switch — isolates selection in 3D when on. */
  fokusTransparency: boolean
  onFokusTransparencyChange: (next: boolean) => void

  /** When true, disables list form edits (comments, export, grouping, Berechnen, …). */
  disableForm?: boolean
}

const Results2ListPane = ({
  items,
  selectedId,
  selectedIds,
  onSelect,
  onSelectionChange,
  primary,
  onPrimaryChange,
  secondary,
  onSecondaryChange,
  showTabLabels = false,
  tabErrorStatuses,
  isStiffeningTab,
  isStandardRipsTab,
  isAssemblySurfaceTab,
  isAnchorsTab,
  isShearAnchorsTab,
  groupingEnabled,
  isLoading = false,
  emptyMessage = 'Keine Daten verfügbar',
  onCalculate,
  onAbort,
  showAbort = false,
  onCopyParentCrossSection,
  copyParentCrossSectionVisibleByChildId,
  copyParentCrossSectionLoadingChildId,
  fokusCamera,
  onFokusCameraChange,
  fokusTransparency,
  onFokusTransparencyChange,
  disableForm = false,
}: Props): ReactElement => {
  const theme = useTheme()
  // Over-utilized tabs: red label on the same orange the list's η values use.
  const errorTabStyle = {
    color: theme.palette.error.main,
    background: OVERUTILIZED_CHIP_BACKGROUND,
  }
  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)
  // Only walls keep a secondary tab strip; Decke/Dach go straight to their beams.
  const showSecondaryTabs = primary === 'walls'
  // The "Anker" tab only exists while the integrated anchor view setting is on; otherwise the
  // anchors live in the Zugverbindertabelle (bottom drawer) as before.
  const wallSecondaryTabs = useMemo(
    () =>
      results2IntegratedAnchorView
        ? WALL_SECONDARY_TABS
        : WALL_SECONDARY_TABS.filter(tab => tab.id !== 'anchors'),
    [results2IntegratedAnchorView],
  )
  const isWallAssemblyTab = primary === 'walls' && secondary === 'wall-assemblies'
  // Foundations reuse the plain assembly list (label only, "-" instead of an assembly).
  const isFoundationTab = primary === 'foundation'
  const showThreeDTargetButtons =
    primary === 'walls' && (secondary === 'wall-rips' || secondary === 'wall-lintels')

  const elementType: ElementTypes | null = useMemo(() => {
    if (isWallAssemblyTab || isStiffeningTab || isStandardRipsTab || isShearAnchorsTab) return null

    if (primary === 'walls') {
      if (secondary === 'wall-rips') return 'rips'
      if (secondary === 'wall-lintels') return 'lintels'
      if (secondary === 'standard-rips') return 'standard_rips'
    }
    // Decke/Dach share one primary tab for two lists: the slab beams (the default the tab opens)
    // and the slab surfaces. Both must report their own type — the list's edits are saved into
    // the position grouping this names.
    if (primary === 'slabs') return isAssemblySurfaceTab ? 'slabs' : 'slab_beams'
    if (primary === 'roof-slabs') return isAssemblySurfaceTab ? 'roof_slabs' : 'roof_slab_beams'
    if (primary === 'beams') return 'beams'
    if (primary === 'purlins') return 'purlins'
    if (primary === 'columns') return 'columns'

    return null
  }, [
    isWallAssemblyTab,
    isStiffeningTab,
    isStandardRipsTab,
    isShearAnchorsTab,
    isAssemblySurfaceTab,
    primary,
    secondary,
  ])

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <TextTabs
        value={primary}
        onChange={onPrimaryChange}
        showLabels={showTabLabels}
        scrollable
        items={PRIMARY_TABS.map(tab => ({
          id: tab.id,
          label: tab.label,
          shortLabel: RESULTS2_TAB_SHORT_LABEL[tab.id],
          ...(tabErrorStatuses?.[tab.id as Results2ErrorTab] ? errorTabStyle : {}),
        }))}
      />

      {showSecondaryTabs ? (
        <TextTabs
          value={secondary}
          onChange={onSecondaryChange}
          showLabels={showTabLabels}
          scrollable
          items={wallSecondaryTabs.map(tab => ({
            id: tab.id,
            label: tab.label,
            shortLabel: RESULTS2_TAB_SHORT_LABEL[tab.id],
            ...(tabErrorStatuses?.[tab.id as Results2ErrorTab] ? errorTabStyle : {}),
          }))}
        />
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {primary === 'comments' ? (
          <CommentsListPane />
        ) : isWallAssemblyTab || isFoundationTab ? (
          <Results2AssemblyList
            items={items}
            selectedId={selectedId}
            selectedIds={selectedIds}
            onSelect={onSelect}
            onSelectionChange={onSelectionChange}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            onCalculate={onCalculate}
            onAbort={onAbort}
            showAbort={showAbort}
            fokusCamera={fokusCamera}
            onFokusCameraChange={onFokusCameraChange}
            fokusTransparency={fokusTransparency}
            onFokusTransparencyChange={onFokusTransparencyChange}
            disableForm={disableForm}
          />
        ) : isAnchorsTab ? (
          <Results2AnchorsList
            items={items}
            selectedId={selectedId}
            selectedIds={selectedIds}
            onSelect={onSelect}
            onSelectionChange={onSelectionChange}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            onCalculate={onCalculate}
            onAbort={onAbort}
            showAbort={showAbort}
            fokusCamera={fokusCamera}
            onFokusCameraChange={onFokusCameraChange}
            fokusTransparency={fokusTransparency}
            onFokusTransparencyChange={onFokusTransparencyChange}
            disableForm={disableForm}
          />
        ) : isShearAnchorsTab ? (
          <Results2ShearAnchorsList
            items={items}
            selectedId={selectedId}
            selectedIds={selectedIds}
            onSelect={onSelect}
            onSelectionChange={onSelectionChange}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            onCalculate={onCalculate}
            onAbort={onAbort}
            showAbort={showAbort}
            fokusCamera={fokusCamera}
            onFokusCameraChange={onFokusCameraChange}
            fokusTransparency={fokusTransparency}
            onFokusTransparencyChange={onFokusTransparencyChange}
            disableForm={disableForm}
          />
        ) : isStiffeningTab || isStandardRipsTab ? (
          <Results2StandardRipsList
            items={items}
            selectedId={selectedId}
            selectedIds={selectedIds}
            onSelect={onSelect}
            onSelectionChange={onSelectionChange}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            onCalculate={onCalculate}
            onAbort={onAbort}
            showAbort={showAbort}
            fokusCamera={fokusCamera}
            onFokusCameraChange={onFokusCameraChange}
            fokusTransparency={fokusTransparency}
            onFokusTransparencyChange={onFokusTransparencyChange}
            disableForm={disableForm}
          />
        ) : (
          <>
            {elementType ? (
              <Results2ElementList
                key={`${primary}:${secondary}`}
                items={items}
                selectedId={selectedId}
                selectedIds={selectedIds}
                onSelect={onSelect}
                onSelectionChange={onSelectionChange}
                multiSelectEnabled={groupingEnabled}
                groupingEnabled={groupingEnabled}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
                showBlockpadButton={false}
                onCalculate={onCalculate}
                onAbort={onAbort}
                showAbort={showAbort}
                onCopyParentCrossSection={groupingEnabled ? onCopyParentCrossSection : undefined}
                copyParentCrossSectionVisibleByChildId={
                  groupingEnabled ? copyParentCrossSectionVisibleByChildId : undefined
                }
                copyParentCrossSectionLoadingChildId={
                  groupingEnabled ? copyParentCrossSectionLoadingChildId : undefined
                }
                showThreeDTargetButtons={showThreeDTargetButtons}
                fokusCamera={fokusCamera}
                onFokusCameraChange={onFokusCameraChange}
                fokusTransparency={fokusTransparency}
                onFokusTransparencyChange={onFokusTransparencyChange}
                disableForm={disableForm}
                elementType={elementType}
              />
            ) : (
              <></>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default Results2ListPane
